<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const { integration_id, provider } = route.query;

const isLoading = ref(true);
const isCreatingChannel = ref(false);
const errorMessage = ref('');
const channelData = ref(null);

const postizUrl = computed(() => {
  return window.chatwootConfig?.postizUrl || 'http://localhost:3700';
});

onMounted(async () => {
  if (!integration_id) {
    errorMessage.value = 'Missing integration ID';
    isLoading.value = false;
    return;
  }

  try {
    // Call Chatwoot backend to proxy Postiz API call
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/postiz/channels`,
      {
        integration_id: integration_id,
        provider: 'youtube'
      }
    );

    const channels = response.data;

    if (!channels || channels.length === 0) {
      errorMessage.value = 'No YouTube channels found for this account';
      isLoading.value = false;
      return;
    }

    // Auto-select the first (and usually only) channel
    channelData.value = channels[0];

    // Automatically create the channel and redirect to add agents
    await createChannelAndRedirect();

  } catch (error) {
    console.error('YouTube callback error:', error);
    errorMessage.value = error.message || 'Failed to load YouTube channel';
    isLoading.value = false;
  }
});

const createChannelAndRedirect = async () => {
  try {
    isCreatingChannel.value = true;

    // Create Chatwoot inbox with YouTube channel
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/inboxes`,
      {
        name: `${channelData.value.name} (YouTube)`,
        channel: {
          type: 'youtube',
          youtube_channel_id: channelData.value.id,
          youtube_channel_name: channelData.value.name,
          youtube_channel_username: channelData.value.username || '',
          youtube_channel_picture: channelData.value.picture?.data?.url || '',
          postiz_integration_id: integration_id,
        }
      }
    );

    const inbox = response.data;

    // Redirect to add agents page (same flow as Instagram/Facebook)
    router.push({
      name: 'settings_inboxes_add_agents',
      params: { accountId, inbox_id: inbox.id }
    });

  } catch (error) {
    console.error('Failed to create YouTube channel:', error);
    errorMessage.value = error.response?.data?.message || 'Failed to create YouTube channel';
    isCreatingChannel.value = false;
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-center h-full">
      <!-- Loading State -->
      <div v-if="isLoading || isCreatingChannel" class="text-center">
        <Spinner />
        <h5 class="text-xl font-semibold mt-4">
          {{ isCreatingChannel ? 'Creating YouTube Channel...' : 'Loading YouTube Channel...' }}
        </h5>
        <p class="text-sm text-n-slate-11 mt-2">
          {{ isCreatingChannel ? 'Setting up your YouTube inbox' : 'Fetching channel information from YouTube' }}
        </p>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="max-w-lg mx-auto text-center">
        <div class="w-20 h-20 mb-6 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <span class="i-ri-error-warning-line text-red-600 text-5xl" />
        </div>
        <h5 class="text-xl font-semibold text-red-600 mb-2">Connection Error</h5>
        <p class="text-sm text-n-slate-11 mb-6">{{ errorMessage }}</p>
        <button
          @click="router.push({ name: 'settings_inbox_new', params: { accountId } })"
          class="px-6 py-3 bg-n-weak hover:bg-n-container text-n-slate-12 rounded-lg transition-all"
        >
          Back to Channel List
        </button>
      </div>

      <!-- Success Preview (shown briefly before redirect) -->
      <div v-else-if="channelData" class="max-w-lg mx-auto text-center">
        <div class="w-20 h-20 mb-6 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <span class="i-ri-youtube-fill text-red-600 text-5xl" />
        </div>
        <h5 class="text-xl font-semibold mb-2">YouTube Channel Connected</h5>
        <p class="text-sm text-n-slate-11 mb-4">{{ channelData.name }}</p>
        <div class="flex items-center justify-center gap-2 text-xs text-n-slate-11">
          <span class="i-ri-user-line" />
          <span>{{ channelData.subscriberCount || '0' }} subscribers</span>
        </div>
      </div>
    </div>
  </div>
</template>
